import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useMutation } from "react-relay";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  JoinAdditionalClubInput,
  useJoinAdditionalClubMutation,
  useJoinAdditionalClubMutation$data,
} from "./__generated__/useJoinAdditionalClubMutation.graphql";

export type JoinAdditionalClubErrors = NonNullable<
  NonNullable<useJoinAdditionalClubMutation$data["responsePayload"]>["errors"]
>;

export const useJoinAdditionalClub = () => {
  const { showToast } = useToast();
  const [joinAdditionalClubMutation, isCreating] =
    useMutation<useJoinAdditionalClubMutation>(
      useJoinAdditionalClubMutationString
    );
  const trackEvent = useTrackEvent();

  const joinAdditionalClub = (
    input: JoinAdditionalClubInput,
    onComplete?: () => void,
    onError?: (errors: JoinAdditionalClubErrors) => void
  ) => {
    joinAdditionalClubMutation({
      variables: { input },
      onCompleted: (res) => {
        if (res.responsePayload?.success) {
          trackEvent("AuthModal.JoinedAdditionalClub", {});
          onComplete && onComplete();
        } else {
          trackEvent("AuthModal.JoinedAdditionalClubError", {
            error: res.responsePayload?.errors,
          });
          const errors = res.responsePayload?.errors;

          if (errors) {
            onError && onError(errors);
          }
        }
      },
      onError: () => {
        showToast({
          description: t`Unable to join the club.`,
          semanticStyle: "error",
          toastId: "join-additional-club-error",
        });
      },
    });
  };

  return [joinAdditionalClub, isCreating] as const;
};

export const useJoinAdditionalClubMutationString = graphql`
  mutation useJoinAdditionalClubMutation($input: JoinAdditionalClubInput!) {
    responsePayload: joinAdditionalClub(input: $input) {
      success
      errors {
        field
        code
        message
      }
    }
  }
`;
